// Blog post title/description rules. The release build writes the same values
// into each post's static head (deploy/release/build-static.mjs): KEEP IN SYNC.

export const BLOG_LANDING_TITLE = 'Number Plate News and Stories | PlateX Blog';
export const BLOG_LANDING_DESCRIPTION =
  'Number plate news and stories from PlateX: record sales, DVLA rules and banned plates, council plate auctions, famous cars and the registrations behind them.';

/** General-news posts with little number plate relevance (launch audit). Owner,
 *  2026-11-03: keep them readable at their addresses, but leave them out of the
 *  blog listings and sitemap and mark them noindex. */
export const BLOG_UNLISTED: string[] = [
  'cupra-tavascan-revolutionising-electric-suvs-in-the-uk',
  'wetherspoons-profits-surge-amid-post-pandemic-recovery',
  'unseen-roger-moore-footage-to-feature-in-new-documentary',
  'usyk-triumphs-over-fury-in-thrilling-rematch',
  'oasis-announces-reunion-tour-after-15-year-hiatus',
  'oxford-uniteds-130-million-stadium-proposal-advances',
  'broomhill-lido-a-historic-treasure-set-to-reopen',
];

export function blogListed(handle: string): boolean {
  return BLOG_UNLISTED.indexOf(handle) < 0;
}

/** Clean a CMS string: drop invisible characters, use plain spaces, no dashes. */
export function cleanBlogText(s: string): string {
  return (s || '')
    .replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u2064\uFEFF]/g, '')
    .replace(/[\u00A0\u2000-\u200A\u202F\u205F\u2800\u3000]/g, ' ')
    .replace(/\s+[\u2013\u2014]\s+/g, ': ')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

const isHeading = (p: string): boolean => {
  const s = (p || '').replace(/<[^>]*>/g, '').trim();
  return s.length < 100 && !/[.!?:]['"\u2019\u201D]?$/.test(s);
};

/** Plain text of a post body (paragraphs, else the html field). */
export function blogPlainText(paragraphs: string[], html: string): string {
  // A short first paragraph without closing punctuation is a heading: skip it.
  const paras = (paragraphs || []).filter((p, i) => !(i === 0 && paragraphs.length > 1 && isHeading(p)));
  const raw = paras.length ? paras.join(' ') : html || '';
  return cleanBlogText(
    raw
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;|&rsquo;|&lsquo;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#(\d+);/g, (_m: string, n: string) => String.fromCharCode(parseInt(n, 10)))
  );
}

/** "{Title} | PlateX Blog" */
export function blogPostTitle(title: string): string {
  return `${cleanBlogText(title)} | PlateX Blog`;
}

/** Summary when it is a full sentence of 70+ characters, else ~150 characters of the body. */
export function blogPostDescription(summary: string, paragraphs: string[], html: string): string {
  const s = cleanBlogText(summary);
  if (s.length >= 70 && /^[A-Z0-9'"\u2018\u201C]/.test(s) && /[.!?]['"\u2019\u201D]?$/.test(s)) return s;
  const body = blogPlainText(paragraphs, html);
  if (body.length <= 155) return body;
  const cut = body.slice(0, 151);
  return cut.slice(0, cut.lastIndexOf(' ')).replace(/[\s,;:.]+$/, '') + '\u2026';
}
